import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, UserPlus, Trash2, RefreshCw, XCircle, Shield, Wand2, Pencil, Check, Globe, Lock, Unlock, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { parseEdgeFunctionError } from "@/lib/parseEdgeFunctionError";
import * as Sentry from "@sentry/react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Workspace {
  id: string;
  name: string;
  subscription_tier: string;
  billing_status: string;
  seat_limit: number;
  created_at: string;
}

interface Member {
  id: string;
  user_id: string;
  role: string;
  status: string;
  accepted_at: string | null;
  email?: string;
  full_name?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  email_sent_at: string | null;
  email_error: string | null;
}

interface WizardConfig {
  id: string;
  name: string;
  type: string;
  region_key: string | null;
}

interface ConfigLink {
  linked_config_id: string;
  linked_type: string;
  linked_config_name: string;
}

export default function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [sending, setSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ url?: string; error?: string; emailSent?: boolean; emailError?: string | null; recipient?: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    | { type: "remove_member"; membershipId: string; email: string }
    | { type: "revoke_invite"; invitationId: string; email: string }
    | null
  >(null);

  // Backoffice runs on a different origin from studio, so construct invite
  // URLs from VITE_STUDIO_URL rather than window.location.
  const studioUrl = (import.meta.env.VITE_STUDIO_URL as string | undefined)?.replace(/\/$/, "") || "http://localhost:8080";
  const inviteUrlFor = (token: string) => `${studioUrl}/invite/${token}`;

  async function copyInviteLink(url: string, id: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 1600);
    } catch (err) {
      console.error("Clipboard write failed:", err);
      setCopiedId(`failed:${id}`);
      window.setTimeout(() => setCopiedId((curr) => (curr === `failed:${id}` ? null : curr)), 2600);
    }
  }

  // Staff join
  const [joining, setJoining] = useState(false);
  const [isStaffMember, setIsStaffMember] = useState(false);

  // Seat limit editing
  const [editingSeats, setEditingSeats] = useState(false);
  const [seatLimitInput, setSeatLimitInput] = useState("");
  const [savingSeats, setSavingSeats] = useState(false);

  // Wizard config assignments (brief-only)
  const [briefWizardConfigs, setBriefWizardConfigs] = useState<WizardConfig[]>([]);
  const [assignedConfigIds, setAssignedConfigIds] = useState<Set<string>>(new Set());
  const [togglingConfigId, setTogglingConfigId] = useState<string | null>(null);
  const [configLinks, setConfigLinks] = useState<Map<string, ConfigLink[]>>(new Map());

  // Region unlocking
  const [allRegions, setAllRegions] = useState<{ region_key: string; display_name: string; bundle_key: string | null }[]>([]);
  const [unlockedRegionKeys, setUnlockedRegionKeys] = useState<Set<string>>(new Set());
  const [togglingRegionKey, setTogglingRegionKey] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadAll();
  }, [id]);

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    await Promise.all([loadWorkspace(), loadCreditBalance(), loadMembers(), loadInvitations(), loadWizardConfigs(), loadRegions()]);
    setLoading(false);
  };

  const loadRegions = async () => {
    if (!id) return;
    const [regionsResult, unlockedResult] = await Promise.all([
      supabase
        .from("regions")
        .select("region_key, display_name, bundle_key")
        .order("display_name"),
      supabase
        .from("workspace_regions")
        .select("region_key")
        .eq("workspace_id", id)
        .eq("status", "active"),
    ]);
    if (regionsResult.error) {
      console.error("Failed to load regions:", regionsResult.error);
      Sentry.captureException(regionsResult.error);
    } else {
      setAllRegions(regionsResult.data || []);
    }
    if (unlockedResult.error) {
      console.error("Failed to load workspace regions:", unlockedResult.error);
      Sentry.captureException(unlockedResult.error);
    } else {
      setUnlockedRegionKeys(new Set((unlockedResult.data || []).map((r: any) => r.region_key)));
    }
  };

  const handleToggleRegion = async (regionKey: string) => {
    if (!id || !user) return;
    setTogglingRegionKey(regionKey);
    const isUnlocked = unlockedRegionKeys.has(regionKey);
    if (isUnlocked) {
      const { error } = await supabase
        .from("workspace_regions")
        .delete()
        .eq("workspace_id", id)
        .eq("region_key", regionKey);
      if (error) {
        console.error("Failed to lock region:", error);
        Sentry.captureException(error);
        alert(`Couldn't lock region: ${error.message}`);
      } else {
        setUnlockedRegionKeys((prev) => {
          const next = new Set(prev);
          next.delete(regionKey);
          return next;
        });
      }
    } else {
      const { error } = await supabase
        .from("workspace_regions")
        .insert({
          workspace_id: id,
          region_key: regionKey,
          status: "active",
          source: "admin",
        } as any);
      if (error) {
        console.error("Failed to unlock region:", error);
        Sentry.captureException(error);
        alert(`Couldn't unlock region: ${error.message}`);
      } else {
        setUnlockedRegionKeys((prev) => new Set(prev).add(regionKey));
      }
    }
    setTogglingRegionKey(null);
  };

  const loadWorkspace = async () => {
    const { data, error } = await supabase
      .from("workspaces")
      .select("*")
      .eq("id", id!)
      .single();
    if (error) {
      console.error("Failed to load workspace:", error);
      Sentry.captureException(error);
      return;
    }
    setWorkspace(data);
  };

  const loadCreditBalance = async () => {
    if (!id) return;
    const { data, error } = await supabase.rpc("get_workspace_credit_balance", {
      p_workspace_id: id,
    });
    if (error) {
      console.error("Failed to load credit balance:", error);
      Sentry.captureException(error);
      return;
    }
    setCreditBalance(typeof data === "number" ? data : 0);
  };

  const loadMembers = async () => {
    const { data: memberships, error } = await supabase
      .from("workspace_memberships")
      .select("*")
      .eq("workspace_id", id!)
      .eq("status", "active")
      .eq("is_deleted", false)
      .order("accepted_at", { ascending: true });

    if (error) {
      console.error("Failed to load members:", error);
      Sentry.captureException(error);
      return;
    }

    // Per-row try/catch so one failing profile lookup doesn't collapse the
    // whole batch (a rejected Promise.all would wipe the table).
    const enriched = await Promise.all(
      (memberships || []).map(async (m) => {
        try {
          const { data: profile, error: profileErr } = await supabase
            .from("profiles")
            .select("email, full_name")
            .eq("user_id", m.user_id)
            .maybeSingle();
          if (profileErr) {
            console.error("Failed to load profile for member", m.user_id, profileErr);
            Sentry.captureException(profileErr);
            return { ...m, email: undefined, full_name: undefined };
          }
          return { ...m, email: profile?.email, full_name: profile?.full_name };
        } catch (err) {
          console.error("Profile lookup threw for member", m.user_id, err);
          Sentry.captureException(err);
          return { ...m, email: undefined, full_name: undefined };
        }
      })
    );

    setMembers(enriched);
    // Check if current user is already a member of this workspace (any role)
    setIsStaffMember(enriched.some((m) => m.user_id === user?.id));
  };

  const loadInvitations = async () => {
    const { data, error } = await supabase
      .from("invitations")
      .select("*")
      .eq("workspace_id", id!)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Failed to load invitations:", error);
      Sentry.captureException(error);
      return;
    }
    setInvitations(data || []);
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !id) return;
    setSending(true);
    setInviteResult(null);

    const { data, error } = await supabase.functions.invoke("send-invitation", {
      body: { workspace_id: id, email: inviteEmail.trim(), role: inviteRole },
    });

    setSending(false);

    if (error) {
      const body = await parseEdgeFunctionError(error);
      console.error("send-invitation error:", error, "body:", body);
      Sentry.captureException(error, { extra: { body } });
      const errMsg = (body.error as string) || "Failed to send invitation";
      setInviteResult({ error: errMsg });
      return;
    }

    setInviteResult({
      url: data.invite_url,
      emailSent: data.email_sent,
      emailError: data.email_error,
      recipient: inviteEmail.trim(),
    });
    setInviteEmail("");
    loadInvitations();
  };

  const handleResendInvite = async (invitationId: string) => {
    const inv = invitations.find((i) => i.id === invitationId);
    if (!inv || !id) return;

    // send-invitation handles revoking old + creating new
    const { data, error } = await supabase.functions.invoke("send-invitation", {
      body: { workspace_id: id, email: inv.email, role: inv.role },
    });

    if (error) {
      const body = await parseEdgeFunctionError(error);
      console.error("Resend failed:", error, "body:", body);
      Sentry.captureException(error, { extra: { body } });
      setInviteResult({ error: (body.error as string) || "Failed to resend invitation" });
      return;
    }

    setInviteResult({
      url: data.invite_url,
      emailSent: data.email_sent,
      emailError: data.email_error,
      recipient: inv.email,
    });
    loadInvitations();
  };

  const requestRevokeInvite = (invitationId: string, email: string) => {
    setConfirmAction({ type: "revoke_invite", invitationId, email });
  };

  const handleRevokeInvite = async (invitationId: string) => {
    const { error } = await supabase.functions.invoke("revoke-invitation", {
      body: { invitation_id: invitationId },
    });
    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Couldn't revoke invitation";
      console.error("Revoke failed:", error, "parsed:", body);
      Sentry.captureException(error, { extra: { body } });
      alert(errMsg);
      return;
    }
    loadInvitations();
  };

  const requestRemoveMember = (membershipId: string, email: string) => {
    setConfirmAction({ type: "remove_member", membershipId, email });
  };

  const updateMemberRole = async (membershipId: string, newRole: string) => {
    const previousMembers = members;
    setMembers((curr) => curr.map((m) => (m.id === membershipId ? { ...m, role: newRole } : m)));

    const { error } = await supabase.functions.invoke("update-member-role", {
      body: { membership_id: membershipId, role: newRole },
    });
    if (error) {
      setMembers(previousMembers);
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Couldn't change role";
      console.error("Update role failed:", error, "parsed:", body);
      Sentry.captureException(error, { extra: { body } });
      alert(errMsg);
      return;
    }
    loadMembers();
  };

  const handleRemoveMember = async (membershipId: string) => {
    const { error } = await supabase.functions.invoke("remove-member", {
      body: { membership_id: membershipId },
    });
    if (error) {
      const body = await parseEdgeFunctionError(error);
      const errMsg = (body.error as string) || "Couldn't remove member";
      console.error("Remove member failed:", error, "parsed:", body);
      Sentry.captureException(error, { extra: { body } });
      alert(errMsg);
      return;
    }
    loadMembers();
  };

  const loadWizardConfigs = async () => {
    const [briefResult, assignedResult, linksResult] = await Promise.all([
      supabase
        .from("wizard_configs")
        .select("id, name, type, region_key")
        .eq("type", "brief")
        .order("name"),
      supabase
        .from("workspace_wizard_configs")
        .select("wizard_config_id")
        .eq("workspace_id", id!),
      supabase
        .from("config_links")
        .select("brief_config_id, linked_config_id, linked_type, wizard_configs!config_links_linked_config_id_fkey(name)")
    ]);

    if (briefResult.error) {
      console.error("Failed to load wizard configs:", briefResult.error);
      Sentry.captureException(briefResult.error);
    } else {
      setBriefWizardConfigs(briefResult.data || []);
    }

    if (assignedResult.error) {
      console.error("Failed to load config assignments:", assignedResult.error);
      Sentry.captureException(assignedResult.error);
    } else {
      setAssignedConfigIds(new Set((assignedResult.data || []).map((r: any) => r.wizard_config_id)));
    }

    if (linksResult.error) {
      console.error("Failed to load config links:", linksResult.error);
      Sentry.captureException(linksResult.error);
    } else if (linksResult.data) {
      const linkMap = new Map<string, ConfigLink[]>();
      for (const link of linksResult.data) {
        const briefId = (link as any).brief_config_id;
        const entry: ConfigLink = {
          linked_config_id: (link as any).linked_config_id,
          linked_type: (link as any).linked_type,
          linked_config_name: (link as any).wizard_configs?.name || "Unknown",
        };
        if (!linkMap.has(briefId)) linkMap.set(briefId, []);
        linkMap.get(briefId)!.push(entry);
      }
      setConfigLinks(linkMap);
    }
  };

  const handleToggleConfig = async (configId: string) => {
    if (!id || !user) return;
    setTogglingConfigId(configId);

    const isAssigned = assignedConfigIds.has(configId);
    if (isAssigned) {
      const { error } = await supabase
        .from("workspace_wizard_configs")
        .delete()
        .eq("workspace_id", id)
        .eq("wizard_config_id", configId);
      if (error) {
        console.error("Failed to unassign config:", error);
        Sentry.captureException(error);
        alert(`Couldn't unassign config: ${error.message}`);
      } else {
        setAssignedConfigIds((prev) => {
          const next = new Set(prev);
          next.delete(configId);
          return next;
        });
      }
    } else {
      const { error } = await supabase
        .from("workspace_wizard_configs")
        .insert({ workspace_id: id, wizard_config_id: configId, assigned_by: user.id });
      if (error) {
        console.error("Failed to assign config:", error);
        Sentry.captureException(error);
        alert(`Couldn't assign config: ${error.message}`);
      } else {
        setAssignedConfigIds((prev) => new Set(prev).add(configId));
      }
    }
    setTogglingConfigId(null);
  };

  const handleSaveSeatLimit = async () => {
    if (!id || !workspace) return;
    const newLimit = parseInt(seatLimitInput, 10);
    if (isNaN(newLimit) || newLimit < 1) return;

    const activeNonStaffCount = members.filter((m) => m.role !== "staff").length;
    if (newLimit < activeNonStaffCount) {
      if (!window.confirm(`Workspace has ${activeNonStaffCount} members. Setting limit to ${newLimit} will put it over capacity. Continue?`)) {
        return;
      }
    }

    setSavingSeats(true);
    const { error } = await supabase
      .from("workspaces")
      .update({ seat_limit: newLimit })
      .eq("id", id);

    setSavingSeats(false);

    if (error) {
      console.error("Failed to update seat limit:", error);
      Sentry.captureException(error);
      alert(`Couldn't update seat limit: ${error.message}`);
      return;
    }

    setWorkspace({ ...workspace, seat_limit: newLimit });
    setEditingSeats(false);
  };

  const handleJoinAsStaff = async () => {
    if (!id || !user) return;
    setJoining(true);

    const { error } = await supabase
      .from("workspace_memberships")
      .insert({
        workspace_id: id,
        user_id: user.id,
        role: "staff",
        status: "active",
        accepted_at: new Date().toISOString(),
      });

    setJoining(false);

    if (error) {
      console.error("Staff join failed:", error);
      Sentry.captureException(error);
      alert(`Couldn't join as staff: ${error.message}`);
      return;
    }

    setIsStaffMember(true);
    loadMembers();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">Workspace not found.</p>
      </div>
    );
  }

  const pendingInvitations = invitations.filter((i) => i.status === "pending");
  const pastInvitations = invitations.filter((i) => i.status !== "pending");

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <button
        onClick={() => navigate("/accounts")}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to accounts
      </button>

      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl font-bold">{workspace.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Created {new Date(workspace.created_at).toLocaleDateString()} &middot;{" "}
            {workspace.subscription_tier} tier &middot;{" "}
            {creditBalance === null ? "—" : creditBalance.toLocaleString()} credits &middot;{" "}
            {members.filter((m) => m.role !== "staff").length}/
            {editingSeats ? (
              <span className="inline-flex items-center gap-1">
                <input
                  type="number"
                  min={1}
                  value={seatLimitInput}
                  onChange={(e) => setSeatLimitInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveSeatLimit();
                    if (e.key === "Escape") { setSeatLimitInput(""); setEditingSeats(false); }
                  }}
                  className="w-14 h-6 px-1 text-sm border border-border rounded bg-background text-foreground"
                  autoFocus
                  disabled={savingSeats}
                />
                <button
                  onClick={handleSaveSeatLimit}
                  disabled={savingSeats}
                  className="text-green-600 hover:text-green-700 disabled:opacity-50"
                  title="Save"
                >
                  {savingSeats ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => { setSeatLimitInput(""); setEditingSeats(false); }}
                  disabled={savingSeats}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                  title="Cancel"
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                {workspace.seat_limit} seats
                <button
                  onClick={() => { setSeatLimitInput(String(workspace.seat_limit)); setEditingSeats(true); }}
                  className="text-muted-foreground hover:text-foreground"
                  title="Edit seat limit"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </span>
            )}
          </p>
        </div>
        {!isStaffMember && (
          <button
            onClick={handleJoinAsStaff}
            disabled={joining}
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Join as Staff
          </button>
        )}
      </div>

      {/* Members */}
      <section className="mb-8">
        <h2 className="font-display text-lg font-semibold mb-3">Members</h2>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Email</th>
                <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Name</th>
                <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Role</th>
                <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Joined</th>
                <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 text-sm">{m.email || "—"}</td>
                  <td className="px-4 py-2 text-sm text-muted-foreground">{m.full_name || "—"}</td>
                  <td className="px-4 py-2">
                    {m.role === "owner" || m.role === "staff" ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        m.role === "owner"
                          ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                      }`}>
                        {m.role}
                      </span>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) => updateMemberRole(m.id, e.target.value)}
                        className="h-7 px-2 rounded-md border border-border bg-background text-xs font-medium focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                        aria-label={`Role for ${m.email || "member"}`}
                      >
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-muted-foreground">
                    {m.accepted_at ? new Date(m.accepted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {m.role !== "owner" && (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => requestRemoveMember(m.id, m.email || "this member")}
                            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                            aria-label="Remove from workspace"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Remove from workspace</TooltipContent>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-muted-foreground">
                    No members yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Send Invitation */}
      <section className="mb-8">
        <h2 className="font-display text-lg font-semibold mb-3">Send Invitation</h2>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground block mb-1">Email</label>
            <input
              type="email"
              placeholder="user@example.com"
              className="w-full px-3 py-2 rounded-md border border-border bg-secondary text-sm"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendInvite()}
            />
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground block mb-1">Role</label>
            <select
              className="w-full px-3 py-2 rounded-md border border-border bg-secondary text-sm"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            onClick={handleSendInvite}
            disabled={sending || !inviteEmail.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Send
          </button>
        </div>
        {inviteResult?.url && inviteResult.emailSent && (
          <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md space-y-2">
            <p className="text-xs text-green-700 dark:text-green-400">
              Invitation email sent to <strong>{inviteResult.recipient}</strong>.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs break-all select-all bg-background/60 border border-green-200 dark:border-green-800 rounded px-2 py-1">
                {inviteResult.url}
              </code>
              <button
                type="button"
                onClick={() => copyInviteLink(inviteResult.url!, "result")}
                className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted/60 shrink-0"
              >
                {copiedId === "result" ? (
                  <><Check className="w-3 h-3" />Copied</>
                ) : copiedId === "failed:result" ? (
                  <><XCircle className="w-3 h-3 text-destructive" />Copy failed</>
                ) : (
                  <><Link2 className="w-3 h-3" />Copy link</>
                )}
              </button>
            </div>
          </div>
        )}
        {inviteResult?.url && inviteResult.emailSent === false && (
          <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md space-y-2">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Invitation created, but the email didn't send
              {inviteResult.emailError ? `: ${inviteResult.emailError}` : "."}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400">Share this link manually:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs break-all select-all bg-background/60 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
                {inviteResult.url}
              </code>
              <button
                type="button"
                onClick={() => copyInviteLink(inviteResult.url!, "result")}
                className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted/60 shrink-0"
              >
                {copiedId === "result" ? (
                  <><Check className="w-3 h-3" />Copied</>
                ) : copiedId === "failed:result" ? (
                  <><XCircle className="w-3 h-3 text-destructive" />Copy failed</>
                ) : (
                  <><Link2 className="w-3 h-3" />Copy link</>
                )}
              </button>
            </div>
          </div>
        )}
        {inviteResult?.error && (
          <p className="mt-2 text-sm text-destructive">{inviteResult.error}</p>
        )}
      </section>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold mb-3">Pending Invitations</h2>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Role</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Expires</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Delivery</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pendingInvitations.map((inv) => (
                  <tr key={inv.id} className="hover:bg-muted/30">
                    <td className="px-4 py-2 text-sm">{inv.email}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground capitalize">{inv.role}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {new Date(inv.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {inv.email_sent_at ? (
                        <span className="text-green-700 dark:text-green-400">
                          Sent {new Date(inv.email_sent_at).toLocaleDateString()}
                        </span>
                      ) : inv.email_error ? (
                        <span
                          className="text-amber-700 dark:text-amber-400"
                          title={inv.email_error}
                        >
                          Failed
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-2 flex gap-1">
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => copyInviteLink(inviteUrlFor(inv.token), inv.id)}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Copy invite link"
                          >
                            {copiedId === inv.id ? (
                              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : copiedId === `failed:${inv.id}` ? (
                              <XCircle className="w-4 h-4 text-destructive" />
                            ) : (
                              <Link2 className="w-4 h-4" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {copiedId === inv.id
                            ? "Copied!"
                            : copiedId === `failed:${inv.id}`
                            ? "Copy failed — select the URL and copy manually"
                            : "Copy invite link"}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleResendInvite(inv.id)}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Resend invitation email"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Resend email (generates a new link and revokes the old one)</TooltipContent>
                      </Tooltip>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => requestRevokeInvite(inv.id, inv.email)}
                            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                            aria-label="Revoke invitation"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Revoke invitation</TooltipContent>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Past Invitations */}
      {pastInvitations.length > 0 && (
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold mb-3 text-muted-foreground">Past Invitations</h2>
          <div className="border border-border rounded-lg overflow-hidden opacity-60">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pastInvitations.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 py-2 text-sm">{inv.email}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.status === "accepted"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : inv.status === "revoked"
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {new Date(inv.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Unlocked Regions */}
      <section className="mb-8">
        <h2 className="font-display text-lg font-semibold mb-1">Unlocked Regions</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Briefs belong to a region (via the wizard config). A user in this workspace can only see briefs for regions that are unlocked here.
        </p>

        {allRegions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No regions configured.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {allRegions.map((region) => {
              const isUnlocked = unlockedRegionKeys.has(region.region_key);
              const isBusy = togglingRegionKey === region.region_key;
              return (
                <button
                  key={region.region_key}
                  onClick={() => handleToggleRegion(region.region_key)}
                  disabled={isBusy}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                    isUnlocked
                      ? "bg-primary/10 border-primary/30 hover:bg-primary/15"
                      : "bg-muted/30 border-border hover:bg-muted/50"
                  } ${isBusy ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isUnlocked ? (
                      <Unlock className="w-4 h-4 text-primary shrink-0" />
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <span className={`text-sm ${isUnlocked ? "font-medium" : "text-muted-foreground"} truncate`}>
                      {region.display_name}
                    </span>
                  </div>
                  {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Brief Wizard Assignments */}
      <section className="mb-8">
        <h2 className="font-display text-lg font-semibold mb-1">Brief Wizards</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Assign brief wizards to this workspace. Product forms and photographer styles are included automatically via config links.
          Wizards tied to a region that isn't unlocked above are disabled.
        </p>

        {briefWizardConfigs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No brief wizard configs exist yet.</p>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {briefWizardConfigs.map((config) => {
              const links = configLinks.get(config.id) || [];
              const productLinks = links.filter((l) => l.linked_type === "product");
              const photographerLinks = links.filter((l) => l.linked_type === "photographer");
              // Region gate: wizard configs without a region are "global" and always available;
              // regional ones require the workspace to have that region unlocked.
              const regionUnlocked = !config.region_key || unlockedRegionKeys.has(config.region_key);
              const isAssigned = assignedConfigIds.has(config.id);
              return (
                <label
                  key={config.id}
                  className={`flex items-start gap-3 px-4 py-3 ${
                    regionUnlocked ? "hover:bg-muted/30 cursor-pointer" : "opacity-60 cursor-not-allowed"
                  }`}
                  title={regionUnlocked ? undefined : "Region not unlocked for this workspace"}
                >
                  <input
                    type="checkbox"
                    checked={isAssigned}
                    onChange={() => regionUnlocked && handleToggleConfig(config.id)}
                    disabled={togglingConfigId === config.id || !regionUnlocked}
                    className="rounded border-border mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Wand2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{config.name}</span>
                      {config.region_key ? (
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded capitalize ${
                            regionUnlocked
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {config.region_key.replace(/_/g, " ")}
                          {!regionUnlocked && " (locked)"}
                        </span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground inline-flex items-center gap-1">
                          <Globe className="w-3 h-3" /> global
                        </span>
                      )}
                    </div>
                    {(productLinks.length > 0 || photographerLinks.length > 0) && (
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                        {productLinks.length > 0 && (
                          <span>Product: {productLinks.map((l) => l.linked_config_name).join(", ")}</span>
                        )}
                        {photographerLinks.length > 0 && (
                          <span>Photographer: {photographerLinks.map((l) => l.linked_config_name).join(", ")}</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </section>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "remove_member" && "Remove from workspace?"}
              {confirmAction?.type === "revoke_invite" && "Revoke invitation?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "remove_member" && (
                <>
                  <strong>{confirmAction.email}</strong> will lose access to this workspace. Their account and any content they created stay intact, and they'll still have access to any other workspaces they're a member of. You can re-invite them later.
                </>
              )}
              {confirmAction?.type === "revoke_invite" && (
                <>
                  The invitation sent to <strong>{confirmAction.email}</strong> will stop working. If they try the link, they'll see "Invalid invitation". You can send a fresh invite later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "remove_member") handleRemoveMember(confirmAction.membershipId);
                if (confirmAction.type === "revoke_invite") handleRevokeInvite(confirmAction.invitationId);
                setConfirmAction(null);
              }}
            >
              {confirmAction?.type === "remove_member" ? "Remove" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
